/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Deterministic fixtures for the
 * forecast summary and details mockups: the series catalogue, the panel-shop
 * bills of materials, outlier rules, run history and a seeded history
 * generator. Dates are relative to today so the demo never goes stale.
 */
import {
  PERSONA_ID,
  getCategory,
  getPerson,
} from '@/demo/procurement/data/company';
import { getMaterial } from '@/demo/procurement/data/materials';
import { startOfToday } from '@/demo/procurement/data/dates';
import type { SiteId } from '@/demo/procurement/data/types';
import type {
  DemandProfileSpec,
  ForecastCategoryId,
  ForecastRun,
  LeadTimeSpec,
  MonthAxis,
  MonthKey,
  SeriesRule,
  SeriesSpec,
} from './forecast-types';

export const HISTORY_MONTHS = 24;
export const HORIZON_MONTHS = 12;

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function monthKeyFor(year: number, monthIndex: number): MonthKey {
  const short = MONTH_SHORT[monthIndex] ?? '';
  return {
    key: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
    short,
    label: `${short} '${String(year).slice(-2)}`,
    monthIndex,
  };
}

/** 24 closed months ending last month, then 12 months starting this one. */
export function buildMonthAxis(now: Date): MonthAxis {
  const at = (offset: number): MonthKey => {
    const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    return monthKeyFor(date.getFullYear(), date.getMonth());
  };
  return {
    history: Array.from({ length: HISTORY_MONTHS }, (_, i) =>
      at(i - HISTORY_MONTHS),
    ),
    forecast: Array.from({ length: HORIZON_MONTHS }, (_, i) => at(i)),
  };
}

export const AXIS: MonthAxis = buildMonthAxis(startOfToday());

// --- Series catalogue ---------------------------------------------------------

export function seriesIdOf(sku: string, siteId: SiteId): string {
  return `${sku}@${siteId}`;
}

export const DEFAULT_SERIES_ID = 'THHN-1/0-CU@DAL';

/** Where the panel shop builds, and where its components are consumed. */
export const PANEL_SHOP_SITE: SiteId = 'PNL';

function profile(
  base: number,
  extra: Partial<DemandProfileSpec> = {},
): DemandProfileSpec {
  return {
    base,
    trend: 0.004,
    seasonAmp: 0.12,
    noise: 0.07,
    occurrence: 1,
    sizeSigma: 0,
    ...extra,
  };
}

function approved(
  daysAgo: number,
  note = 'Within tolerance of the last update',
): SeriesSpec['approval'] {
  return { status: 'approved', note, decidedDaysAgo: daysAgo };
}

function needsApproval(note: string): SeriesSpec['approval'] {
  return { status: 'needs_approval', note, decidedDaysAgo: null };
}

function measured(
  days: number,
  samples: number,
  sigmaDays: number,
): LeadTimeSpec {
  return { days, source: 'measured', samples, sigmaDays };
}

function assumed(days: number, sigmaDays: number): LeadTimeSpec {
  return { days, source: 'assumed', samples: 0, sigmaDays };
}

/** The panel shop's three finished goods, forecast 12 months ahead. */
export const FG_SKUS = ['PPC-MCC-480', 'PPC-PDP-208', 'KIT-VFD-50'] as const;
export type FinishedGoodSku = (typeof FG_SKUS)[number];

export function isFinishedGoodSku(sku: string): sku is FinishedGoodSku {
  return FG_SKUS.some((fg) => fg === sku);
}

/** Finished-goods forecast. The first six months are the canonical plan. */
export const FG_PLAN: Record<FinishedGoodSku, number[]> = {
  'PPC-MCC-480': [18, 22, 26, 24, 20, 30, 32, 34, 31, 27, 23, 21],
  'PPC-PDP-208': [40, 38, 44, 46, 42, 48, 50, 52, 49, 45, 43, 41],
  'KIT-VFD-50': [60, 55, 70, 65, 75, 80, 82, 78, 72, 66, 62, 58],
};

export const FG_DESCRIPTION: Record<FinishedGoodSku, string> = {
  'PPC-MCC-480': 'Motor control panel, 480 V, 6 sections',
  'PPC-PDP-208': 'Power distribution panel, 208 V',
  'KIT-VFD-50': 'Motor drive retrofit kit, 50 HP',
};

export interface BomLine {
  component: string;
  finishedGood: FinishedGoodSku;
  /** Quantity of the component per finished good. */
  qtyPer: number;
}

export const BOM_LINES: BomLine[] = [
  { component: 'ENC-N12-7224', finishedGood: 'PPC-MCC-480', qtyPer: 1 },
  { component: 'ENC-N12-7224', finishedGood: 'PPC-PDP-208', qtyPer: 1 },
  { component: 'ENC-N4X-2420', finishedGood: 'KIT-VFD-50', qtyPer: 1 },
  { component: 'MCCB-800A', finishedGood: 'PPC-MCC-480', qtyPer: 1 },
  { component: 'MCCB-800A', finishedGood: 'PPC-PDP-208', qtyPer: 1 },
  { component: 'VFD-50HP-480', finishedGood: 'PPC-MCC-480', qtyPer: 6 },
  { component: 'VFD-50HP-480', finishedGood: 'KIT-VFD-50', qtyPer: 1 },
  { component: 'MCB-20A-1P', finishedGood: 'PPC-PDP-208', qtyPer: 24 },
  { component: 'THHN-1/0-CU', finishedGood: 'PPC-MCC-480', qtyPer: 4 },
  { component: 'THHN-1/0-CU', finishedGood: 'PPC-PDP-208', qtyPer: 3 },
  { component: 'THHN-1/0-CU', finishedGood: 'KIT-VFD-50', qtyPer: 0.5 },
];

export function bomForComponent(sku: string): BomLine[] {
  return BOM_LINES.filter((line) => line.component === sku);
}

export function bomForFinishedGood(sku: string): BomLine[] {
  return BOM_LINES.filter((line) => line.finishedGood === sku);
}

const PANEL_SHOP_NOTE =
  'Panel-shop builds are valued at assembly cost only; their parts appear on their own lines.';

export { PANEL_SHOP_NOTE };

export const SERIES_SPECS: SeriesSpec[] = [
  // Wire and cable
  {
    sku: 'THHN-1/0-CU',
    siteId: 'DAL',
    description: 'Copper building wire, 1/0 gauge, 500 ft reels',
    categoryId: 'cat-wire',
    unit: 'C',
    unitCost: 432.08,
    materialId: 'MAT-THHN',
    supplierIds: ['sup-corvane', 'sup-hollis', 'sup-kaisei'],
    profile: profile(7600, { trend: 0.004, seasonAmp: 0.18, noise: 0.06 }),
    spike: { monthsAgo: 6, multiplier: 3.1 },
    leadTime: measured(58, 42, 9),
    targetAvailability: 0.975,
    approval: approved(
      4,
      'Auto-approved, change vs last update +1.8%. The April spike is flagged for a decision.',
    ),
    story:
      'The Riverside Stadium wire pull in the spike month was a one-time project order, not run-rate demand.',
  },
  {
    sku: 'THHN-1/0-CU',
    siteId: 'PHX',
    description: 'Copper building wire, 1/0 gauge, 500 ft reels',
    categoryId: 'cat-wire',
    unit: 'C',
    unitCost: 432.08,
    materialId: 'MAT-THHN',
    supplierIds: ['sup-corvane', 'sup-hollis', 'sup-kaisei'],
    profile: profile(3700, { seasonAmp: 0.16, noise: 0.07 }),
    leadTime: measured(58, 31, 9),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'THHN-1/0-CU',
    siteId: 'PNL',
    description: 'Copper building wire, 1/0 gauge, 500 ft reels',
    categoryId: 'cat-wire',
    unit: 'C',
    unitCost: 432.08,
    materialId: 'MAT-THHN',
    supplierIds: ['sup-corvane', 'sup-hollis'],
    profile: profile(240, { seasonAmp: 0.1, noise: 0.05 }),
    derivedFromBoms: true,
    leadTime: measured(58, 26, 9),
    targetAvailability: 0.95,
    approval: approved(6),
  },
  {
    sku: 'THHN-12-CU',
    siteId: 'DAL',
    description: 'Copper building wire, 12 gauge, stranded, 500 ft reels',
    categoryId: 'cat-wire',
    unit: 'C',
    unitCost: 38.4,
    supplierIds: ['sup-hollis'],
    profile: profile(22000, { seasonAmp: 0.14, noise: 0.06 }),
    leadTime: measured(35, 51, 5),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'THHN-4-CU',
    siteId: 'HAY',
    description: 'Copper building wire, 4 gauge, stranded, 500 ft reels',
    categoryId: 'cat-wire',
    unit: 'C',
    unitCost: 118.2,
    supplierIds: ['sup-corvane'],
    profile: profile(2100, { seasonAmp: 0.05, noise: 0, sizeSigma: 1.0 }),
    leadTime: measured(56, 22, 10),
    targetAvailability: 0.95,
    approval: approved(7),
  },
  {
    sku: 'XHHW-500-AL',
    siteId: 'PHX',
    description: 'Aluminum feeder cable, 500 kcmil, per 100 ft',
    categoryId: 'cat-wire',
    unit: 'C',
    unitCost: 352,
    supplierIds: ['sup-corvane'],
    profile: profile(640, {
      seasonAmp: 0.1,
      occurrence: 0.55,
      sizeSigma: 0.35,
    }),
    leadTime: assumed(56, 14),
    targetAvailability: 0.95,
    approval: approved(7),
  },
  // Circuit protection
  {
    sku: 'MCCB-800A',
    siteId: 'DAL',
    description: 'Circuit breaker, 800 A',
    categoryId: 'cat-circuit',
    unit: 'each',
    unitCost: 3884,
    materialId: 'MAT-MCCB',
    supplierIds: ['sup-vitrex', 'sup-hollis'],
    profile: profile(165, { seasonAmp: 0.08, noise: 0.08 }),
    leadTime: measured(70, 24, 8),
    targetAvailability: 0.975,
    approval: approved(5),
  },
  {
    sku: 'MCCB-800A',
    siteId: 'PNL',
    description: 'Circuit breaker, 800 A',
    categoryId: 'cat-circuit',
    unit: 'each',
    unitCost: 3884,
    materialId: 'MAT-MCCB',
    supplierIds: ['sup-vitrex', 'sup-hollis'],
    profile: profile(62, { noise: 0.05 }),
    derivedFromBoms: true,
    leadTime: measured(70, 24, 8),
    targetAvailability: 0.99,
    approval: approved(2, 'Tracks the panel build plan; quality hold noted'),
  },
  {
    sku: 'MCB-20A-1P',
    siteId: 'DAL',
    description: 'Small circuit breaker, 20 A, 1 pole, bolt-on',
    categoryId: 'cat-circuit',
    unit: 'each',
    unitCost: 8.6,
    supplierIds: ['sup-hollis'],
    profile: profile(2600, { seasonAmp: 0.12, noise: 0.06 }),
    leadTime: measured(35, 63, 4),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'MCB-20A-1P',
    siteId: 'PHX',
    description: 'Small circuit breaker, 20 A, 1 pole, bolt-on',
    categoryId: 'cat-circuit',
    unit: 'each',
    unitCost: 8.6,
    supplierIds: ['sup-hollis'],
    profile: profile(1300, { seasonAmp: 0.12, noise: 0.07 }),
    leadTime: measured(35, 40, 4),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'MCB-20A-1P',
    siteId: 'HAY',
    description: 'Small circuit breaker, 20 A, 1 pole, bolt-on',
    categoryId: 'cat-circuit',
    unit: 'each',
    unitCost: 8.6,
    supplierIds: ['sup-hollis'],
    profile: profile(1100, { seasonAmp: 0.1, noise: 0.08 }),
    leadTime: measured(35, 36, 4),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'MCB-20A-1P',
    siteId: 'PNL',
    description: 'Small circuit breaker, 20 A, 1 pole, bolt-on',
    categoryId: 'cat-circuit',
    unit: 'each',
    unitCost: 8.6,
    supplierIds: ['sup-hollis'],
    profile: profile(1000, { noise: 0.05 }),
    derivedFromBoms: true,
    leadTime: measured(35, 29, 4),
    targetAvailability: 0.95,
    approval: approved(3),
  },
  {
    sku: 'MCCB-250A',
    siteId: 'DAL',
    description: 'Circuit breaker, 250 A',
    categoryId: 'cat-circuit',
    unit: 'each',
    unitCost: 890,
    supplierIds: ['sup-hollis'],
    profile: profile(46, {
      seasonAmp: 0.05,
      occurrence: 0.5,
      sizeSigma: 1.1,
    }),
    leadTime: measured(35, 14, 6),
    targetAvailability: 0.95,
    approval: approved(7),
  },
  {
    sku: 'DISC-200A-NF3',
    siteId: 'PHX',
    description: 'Fused disconnect switch, 200 A, 3-pole, outdoor rated',
    categoryId: 'cat-circuit',
    unit: 'each',
    unitCost: 640,
    supplierIds: ['sup-hollis'],
    profile: profile(24, { seasonAmp: 0.1, occurrence: 0.6, sizeSigma: 0.35 }),
    leadTime: assumed(28, 7),
    targetAvailability: 0.95,
    approval: approved(7),
  },
  // Lighting
  {
    sku: 'LED-HB150-PP',
    siteId: 'DAL',
    description: 'Pillar Pro LED high-bay fixture, 150 W',
    categoryId: 'cat-lighting',
    unit: 'fixture',
    unitCost: 89.1,
    materialId: 'MAT-LED',
    supplierIds: ['sup-aurelia', 'sup-northgate'],
    profile: profile(3900, { seasonAmp: 0.14, noise: 0.06 }),
    leadTime: measured(42, 58, 6),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'LED-HB150-PP',
    siteId: 'PHX',
    description: 'Pillar Pro LED high-bay fixture, 150 W',
    categoryId: 'cat-lighting',
    unit: 'fixture',
    unitCost: 89.1,
    materialId: 'MAT-LED',
    supplierIds: ['sup-aurelia', 'sup-northgate'],
    profile: profile(3300, { seasonAmp: 0.12, noise: 0.06 }),
    leadTime: measured(42, 44, 6),
    targetAvailability: 0.975,
    seedFactor: {
      factor: 1.08,
      fromIndex: 1,
      reason:
        'Phoenix data-center build-out: three halls getting new lighting over the next two quarters',
      addedDaysAgo: 3,
    },
    approval: needsApproval(
      'Growth adjustment of +8% added 3 days ago needs sign-off',
    ),
    story:
      'The +8% growth adjustment reflects the data-center build-out in Phoenix, which the history cannot see yet.',
  },
  {
    sku: 'LED-HB150-PP',
    siteId: 'HAY',
    description: 'Pillar Pro LED high-bay fixture, 150 W',
    categoryId: 'cat-lighting',
    unit: 'fixture',
    unitCost: 89.1,
    materialId: 'MAT-LED',
    supplierIds: ['sup-aurelia', 'sup-northgate'],
    profile: profile(3100, { seasonAmp: 0.1, noise: 0.07 }),
    leadTime: measured(42, 41, 6),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'LED-PNL-2X4-PP',
    siteId: 'DAL',
    description: 'Pillar Pro LED flat panel, 2x4 ft, 40 W',
    categoryId: 'cat-lighting',
    unit: 'each',
    unitCost: 31.8,
    supplierIds: ['sup-aurelia', 'sup-northgate'],
    profile: profile(7800, { seasonAmp: 0.12, noise: 0.07 }),
    leadTime: measured(42, 47, 6),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'LED-PNL-2X4-PP',
    siteId: 'PHX',
    description: 'Pillar Pro LED flat panel, 2x4 ft, 40 W',
    categoryId: 'cat-lighting',
    unit: 'each',
    unitCost: 31.8,
    supplierIds: ['sup-aurelia', 'sup-northgate'],
    profile: profile(4500, { seasonAmp: 0.1, noise: 0.07 }),
    leadTime: measured(42, 33, 6),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'LED-WP-25-PP',
    siteId: 'HAY',
    description: 'Pillar Pro LED wall light, 25 W',
    categoryId: 'cat-lighting',
    unit: 'each',
    unitCost: 38.4,
    supplierIds: ['sup-aurelia'],
    profile: profile(1400, { seasonAmp: 0.05, noise: 0, sizeSigma: 1.0 }),
    leadTime: measured(42, 19, 7),
    targetAvailability: 0.95,
    approval: approved(7),
  },
  // Conduit and raceway
  {
    sku: 'PVC-40-1IN',
    siteId: 'DAL',
    description: 'PVC conduit, 1 in x 10 ft',
    categoryId: 'cat-conduit',
    unit: 'stick',
    unitCost: 4.6,
    materialId: 'MAT-PVC',
    supplierIds: ['sup-meridian'],
    profile: profile(199500, { seasonAmp: 0.2, noise: 0.06 }),
    leadTime: measured(21, 88, 3),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'PVC-40-2IN',
    siteId: 'DAL',
    description: 'PVC conduit, 2 in x 10 ft',
    categoryId: 'cat-conduit',
    unit: 'stick',
    unitCost: 11.4,
    supplierIds: ['sup-meridian'],
    profile: profile(9000, { seasonAmp: 0.2, noise: 0.07 }),
    leadTime: measured(21, 64, 3),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'EMT-3/4-10',
    siteId: 'DAL',
    description: 'Steel conduit, 3/4 in x 10 ft',
    categoryId: 'cat-conduit',
    unit: 'stick',
    unitCost: 6.05,
    supplierIds: ['sup-meridian'],
    profile: profile(24000, { seasonAmp: 0.16, noise: 0.06 }),
    leadTime: measured(21, 72, 3),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  {
    sku: 'EMT-3/4-10',
    siteId: 'PHX',
    description: 'Steel conduit, 3/4 in x 10 ft',
    categoryId: 'cat-conduit',
    unit: 'stick',
    unitCost: 6.05,
    supplierIds: ['sup-meridian'],
    profile: profile(12000, { seasonAmp: 0.14, noise: 0.07 }),
    leadTime: measured(21, 49, 3),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  // Drives and automation
  {
    sku: 'VFD-50HP-480',
    siteId: 'HAY',
    description: 'Motor drive, 50 HP, 480 V',
    categoryId: 'cat-drives',
    unit: 'each',
    unitCost: 5500,
    materialId: 'MAT-VFD',
    supplierIds: ['sup-lumen'],
    profile: profile(85, { seasonAmp: 0.04, noise: 0, sizeSigma: 1.0 }),
    leadTime: measured(84, 18, 11),
    targetAvailability: 0.975,
    approval: approved(5),
  },
  {
    sku: 'VFD-50HP-480',
    siteId: 'PNL',
    description: 'Motor drive, 50 HP, 480 V',
    categoryId: 'cat-drives',
    unit: 'each',
    unitCost: 5500,
    materialId: 'MAT-VFD',
    supplierIds: ['sup-lumen'],
    profile: profile(190, { noise: 0.04 }),
    derivedFromBoms: true,
    leadTime: measured(84, 18, 11),
    targetAvailability: 0.99,
    approval: needsApproval(
      'The model forecast sits {bomGap} the panel build plan',
    ),
    story:
      'Demand here is the panel shop pulling drives: six per motor control panel plus one per retrofit kit.',
  },
  {
    sku: 'VFD-10HP-480',
    siteId: 'HAY',
    description: 'Motor drive, 10 HP, 480 V',
    categoryId: 'cat-drives',
    unit: 'each',
    unitCost: 1320,
    supplierIds: ['sup-lumen'],
    profile: profile(150, { seasonAmp: 0.1, noise: 0.1 }),
    leadTime: measured(84, 16, 11),
    targetAvailability: 0.975,
    approval: approved(5),
  },
  // Enclosures and panel components
  {
    sku: 'ENC-N4X-2420',
    siteId: 'PNL',
    description: 'Corrosion-resistant stainless enclosure, 24 x 20 x 8 in',
    categoryId: 'cat-enclosures',
    unit: 'each',
    unitCost: 612,
    supplierIds: ['sup-polaris'],
    profile: profile(60, { noise: 0.05 }),
    derivedFromBoms: true,
    leadTime: measured(42, 20, 5),
    targetAvailability: 0.975,
    approval: approved(3),
  },
  {
    sku: 'ENC-N12-7224',
    siteId: 'PNL',
    description: 'Floor-standing enclosure, 72 x 24 x 24 in',
    categoryId: 'cat-enclosures',
    unit: 'each',
    unitCost: 1480,
    supplierIds: ['sup-polaris'],
    profile: profile(62, { noise: 0.05 }),
    derivedFromBoms: true,
    leadTime: measured(42, 23, 5),
    targetAvailability: 0.975,
    approval: approved(3),
  },
  {
    sku: 'ENC-N4X-2420',
    siteId: 'DAL',
    description: 'Corrosion-resistant stainless enclosure, 24 x 20 x 8 in',
    categoryId: 'cat-enclosures',
    unit: 'each',
    unitCost: 612,
    supplierIds: ['sup-polaris'],
    profile: profile(38, { occurrence: 0.6, sizeSigma: 0.35 }),
    leadTime: assumed(42, 7),
    targetAvailability: 0.95,
    approval: approved(7),
  },
  {
    sku: 'PPC-MCC-480',
    siteId: 'PNL',
    description: FG_DESCRIPTION['PPC-MCC-480'],
    categoryId: 'cat-enclosures',
    unit: 'panel',
    unitCost: 6400,
    supplierIds: [],
    externalVendor: 'Pillar control panel shop',
    profile: profile(22, { trend: 0.012, seasonAmp: 0.15, noise: 0.14 }),
    modelForecast: FG_PLAN['PPC-MCC-480'],
    leadTime: measured(15, 40, 3),
    targetAvailability: 0.95,
    approval: needsApproval(
      'Six-month build plan raised 12% after two new motor control orders',
    ),
    story:
      'Panels and kits come from the build plan, which is then broken down by bill of materials into parts demand.',
  },
  {
    sku: 'PPC-PDP-208',
    siteId: 'PNL',
    description: FG_DESCRIPTION['PPC-PDP-208'],
    categoryId: 'cat-enclosures',
    unit: 'panel',
    unitCost: 1100,
    supplierIds: [],
    externalVendor: 'Pillar control panel shop',
    profile: profile(39, { trend: 0.008, seasonAmp: 0.12, noise: 0.1 }),
    modelForecast: FG_PLAN['PPC-PDP-208'],
    leadTime: measured(10, 44, 2),
    targetAvailability: 0.95,
    approval: approved(2),
  },
  {
    sku: 'KIT-VFD-50',
    siteId: 'PNL',
    description: FG_DESCRIPTION['KIT-VFD-50'],
    categoryId: 'cat-enclosures',
    unit: 'kit',
    unitCost: 950,
    supplierIds: [],
    externalVendor: 'Pillar control panel shop',
    profile: profile(60, { trend: 0.018, seasonAmp: 0.14, noise: 0.1 }),
    modelForecast: FG_PLAN['KIT-VFD-50'],
    leadTime: measured(6, 58, 1),
    targetAvailability: 0.95,
    approval: approved(2),
  },
  // Transformers and power distribution
  {
    sku: 'XFMR-75KVA-480-208',
    siteId: 'DAL',
    description: 'Dry-type transformer, 75 kVA, 480 V to 208 V',
    categoryId: 'cat-power',
    unit: 'each',
    unitCost: 4950,
    supplierIds: ['sup-stellar'],
    profile: profile(6, { seasonAmp: 0.1, occurrence: 0.5, sizeSigma: 1.1 }),
    leadTime: assumed(360, 40),
    targetAvailability: 0.95,
    approval: approved(
      7,
      'Delivery time over 50 weeks; demand is project-driven',
    ),
  },
  {
    sku: 'XFMR-30KVA-480-208',
    siteId: 'PHX',
    description: 'Dry-type transformer, 30 kVA, 480 V to 208 V',
    categoryId: 'cat-power',
    unit: 'each',
    unitCost: 2280,
    supplierIds: ['sup-stellar'],
    profile: profile(5, { seasonAmp: 0.1, occurrence: 0.6, sizeSigma: 0.35 }),
    leadTime: assumed(360, 40),
    targetAvailability: 0.95,
    approval: approved(7),
  },
  // Wiring devices
  {
    sku: 'DEV-REC-15A-WH',
    siteId: 'DAL',
    description: 'Wall outlet, 15 A, 125 V, white, box of 10',
    categoryId: 'cat-devices',
    unit: 'box',
    unitCost: 6.8,
    supplierIds: ['sup-clearwell'],
    profile: profile(5200, { seasonAmp: 0.08, noise: 0.06 }),
    leadTime: measured(28, 77, 3),
    targetAvailability: 0.975,
    approval: approved(4),
  },
  // MRO items carried over from the Replenishment demo
  {
    sku: 'HQC-0500',
    siteId: 'DAL',
    description: 'Hydraulic quick coupler, 1/2 in, flat-face',
    categoryId: 'cat-mro',
    unit: 'each',
    unitCost: 36,
    supplierIds: ['sup-mro-supply'],
    profile: profile(95, { trend: 0.006, seasonAmp: 0.04, noise: 0.08 }),
    spike: { monthsAgo: 6, multiplier: 6.4 },
    modelForecast: [112, 115, 118, 121, 124, 127, 130, 133, 136, 139, 142, 145],
    leadTime: measured(14, 6, 2),
    targetAvailability: 0.95,
    approval: approved(
      2,
      'Auto-approved. The April spike is flagged for a decision; baseline near 95 a month without it.',
    ),
    story:
      'Same Riverside Stadium job as the wire spike: one project build of couplers, flagged so a buyer can leave it out of the baseline.',
  },
  {
    sku: 'BRE-0090',
    siteId: 'DAL',
    description: 'Brass elbow, 90 degree, 3/4 in threaded',
    categoryId: 'cat-mro',
    unit: 'each',
    unitCost: 11.5,
    supplierIds: ['sup-mro-supply'],
    profile: profile(230, { trend: 0.012, seasonAmp: 0.04, noise: 0.06 }),
    modelForecast: [262, 266, 270, 274, 278, 282, 286, 290, 294, 298, 302, 306],
    leadTime: measured(12, 8, 2),
    targetAvailability: 0.95,
    approval: approved(2),
  },
  {
    sku: 'GVB-2210',
    siteId: 'PHX',
    description: 'Galvanized mounting bracket, heavy duty',
    categoryId: 'cat-mro',
    unit: 'each',
    unitCost: 22.4,
    supplierIds: ['sup-mro-supply'],
    profile: profile(60, { seasonAmp: 0.06, noise: 0.08 }),
    leadTime: measured(10, 5, 2),
    targetAvailability: 0.95,
    approval: approved(2),
  },
  {
    sku: 'CUL-0033',
    siteId: 'DAL',
    description: 'Copper wire lug, 4 gauge',
    categoryId: 'cat-mro',
    unit: 'each',
    unitCost: 2.85,
    supplierIds: [],
    externalVendor: 'Lone Star Electric Supply',
    profile: profile(480, { seasonAmp: 0.2, noise: 0.07 }),
    leadTime: measured(9, 12, 2),
    targetAvailability: 0.95,
    approval: approved(2),
  },
];

export const SERIES_IDS: string[] = SERIES_SPECS.map((spec) =>
  seriesIdOf(spec.sku, spec.siteId),
);

const SPEC_BY_ID = new Map(
  SERIES_SPECS.map((spec) => [seriesIdOf(spec.sku, spec.siteId), spec]),
);

export function getSpec(id: string): SeriesSpec | undefined {
  return SPEC_BY_ID.get(id);
}

export function specId(spec: SeriesSpec): string {
  return seriesIdOf(spec.sku, spec.siteId);
}

/** The item description; the material's own name where one is linked. */
export function describeSpec(spec: SeriesSpec): string {
  return spec.materialId ? getMaterial(spec.materialId).name : spec.description;
}

export function categoryName(categoryId: ForecastCategoryId): string {
  return getCategory(categoryId).name;
}

export const FORECAST_SITES: SiteId[] = ['DAL', 'PHX', 'HAY', 'PNL'];

export function forecastCategories(): ForecastCategoryId[] {
  const seen = new Set<ForecastCategoryId>();
  for (const spec of SERIES_SPECS) seen.add(spec.categoryId);
  return [...seen];
}

// --- Outlier rules ---------------------------------------------------------------

export const OUTLIER_RULES: SeriesRule[] = [
  {
    id: 'OR-014',
    scope: 'item_loc',
    seriesIds: ['THHN-1/0-CU@DAL'],
    params: {
      method: 'std_dev',
      threshold: 3,
      action: 'flag',
      windowMonths: 24,
    },
    description:
      'Flags the one-time Riverside Stadium wire pull. Leaving it out reads the month as a typical month.',
    createdBy: getPerson(PERSONA_ID).name,
    createdDaysAgo: 18,
  },
  {
    id: 'OR-015',
    scope: 'item_loc',
    seriesIds: ['HQC-0500@DAL'],
    params: {
      method: 'std_dev',
      threshold: 3,
      action: 'flag',
      windowMonths: 24,
    },
    description:
      'Flags the same stadium project: a one-time coupler build a buyer can leave out of the baseline.',
    createdBy: getPerson(PERSONA_ID).name,
    createdDaysAgo: 18,
  },
  {
    id: 'OR-007',
    scope: 'organization',
    seriesIds: [],
    params: {
      method: 'median_multiple',
      threshold: 8,
      action: 'cap',
      windowMonths: 24,
    },
    description:
      'Default guard for every item without a more specific rule: cap any month above 8 times the typical month.',
    createdBy: getPerson('marcus-hale').name,
    createdDaysAgo: 210,
  },
];

export function getRule(id: string): SeriesRule | undefined {
  return OUTLIER_RULES.find((rule) => rule.id === id);
}

// --- Forecast runs ---------------------------------------------------------------

function measuredShare(): number {
  const measuredCount = SERIES_SPECS.filter(
    (spec) => spec.leadTime.source === 'measured',
  ).length;
  return measuredCount / SERIES_SPECS.length;
}

export function runs(): ForecastRun[] {
  const total = SERIES_SPECS.length;
  const heldForApproval = SERIES_SPECS.filter(
    (spec) => spec.approval.status === 'needs_approval',
  ).length;
  const share = measuredShare();
  return [
    {
      id: 'RUN-0',
      startedDaysAgo: 0,
      durationSeconds: 252,
      status: 'completed',
      purpose: 'Nightly update',
      seriesCount: total,
      policiesUpdated: total - heldForApproval,
      measuredLeadTimeShare: share,
      note: `${heldForApproval} stock levels held until the forecast is approved`,
    },
    {
      id: 'RUN-1',
      startedDaysAgo: 1,
      durationSeconds: 247,
      status: 'completed',
      purpose: 'Nightly update',
      seriesCount: total,
      policiesUpdated: total - 1,
      measuredLeadTimeShare: share,
    },
    {
      id: 'RUN-2',
      startedDaysAgo: 2,
      durationSeconds: 255,
      status: 'completed',
      purpose: 'Nightly update',
      seriesCount: total,
      policiesUpdated: total,
      measuredLeadTimeShare: share - 0.02,
    },
    {
      id: 'RUN-3',
      startedDaysAgo: 3,
      durationSeconds: 141,
      status: 'partial',
      purpose: 'Nightly update',
      seriesCount: total - 6,
      policiesUpdated: total - 6,
      measuredLeadTimeShare: share - 0.02,
      note: 'Hayward DC held back: the sales history file arrived late and failed the data check',
    },
    {
      id: 'RUN-4',
      startedDaysAgo: 4,
      durationSeconds: 9,
      status: 'completed',
      purpose: 'Recalculated after a manual change',
      seriesCount: 1,
      policiesUpdated: 1,
      measuredLeadTimeShare: 1,
      note: 'LED-HB150-PP@PHX after the growth adjustment was added',
    },
    {
      id: 'RUN-5',
      startedDaysAgo: 5,
      durationSeconds: 249,
      status: 'completed',
      purpose: 'Nightly update',
      seriesCount: total,
      policiesUpdated: total,
      measuredLeadTimeShare: share - 0.04,
    },
  ];
}

// --- Seeded history ---------------------------------------------------------------

/** Construction season: low in winter, peaking late summer. Zero mean. */
const SEASON_CURVE: number[] = (() => {
  const shape = [
    -0.9, -0.8, -0.4, 0.1, 0.5, 0.8, 0.9, 1.0, 0.6, 0.2, -0.4, -0.9,
  ];
  const avg = shape.reduce((total, value) => total + value, 0) / shape.length;
  return shape.map((value) => value - avg);
})();

export function seasonCurve(monthIndex: number): number {
  return SEASON_CURVE[monthIndex] ?? 0;
}

function hashString(text: string): number {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function gaussian(rand: () => number): number {
  const u = Math.max(rand(), 1e-9);
  const v = rand();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function generateHistory(spec: SeriesSpec): number[] {
  const rand = mulberry32(hashString(specId(spec)));
  const { base, trend, seasonAmp, noise, occurrence, sizeSigma } = spec.profile;
  const values = AXIS.history.map((month, t) => {
    const level = base * (1 + trend * (t - 17.5));
    const season = 1 + seasonAmp * seasonCurve(month.monthIndex);
    // Two draws every month so the stream never shifts with the branch taken.
    const chance = rand();
    const z = gaussian(rand);
    const logNormal = Math.exp(sizeSigma * z - (sizeSigma * sizeSigma) / 2);
    if (occurrence < 1) {
      const size = ((level * season) / occurrence) * logNormal;
      return chance < occurrence ? Math.max(1, Math.round(size)) : 0;
    }
    const factor = sizeSigma > 0 ? logNormal : 1 + noise * z;
    return Math.max(0, Math.round(level * season * factor));
  });
  if (spec.spike) {
    const index = HISTORY_MONTHS - spec.spike.monthsAgo;
    values[index] = Math.round((values[index] ?? 0) * spec.spike.multiplier);
  }
  return values;
}

const HISTORY_CACHE = new Map<string, number[]>();

/**
 * Recorded actuals for a series, 24 closed months. Panel-shop components are
 * built from the finished-goods history through the bill of materials, so their
 * demand moves with the panels the shop built.
 */
export function rawHistoryFor(spec: SeriesSpec): number[] {
  const id = specId(spec);
  const cached = HISTORY_CACHE.get(id);
  if (cached) return cached;
  const history = spec.derivedFromBoms
    ? generateDerivedHistory(spec)
    : generateHistory(spec);
  HISTORY_CACHE.set(id, history);
  return history;
}

function generateDerivedHistory(spec: SeriesSpec): number[] {
  const rand = mulberry32(hashString(`${specId(spec)}:derived`));
  const lines = bomForComponent(spec.sku);
  const fgHistory = new Map<string, number[]>();
  for (const line of lines) {
    const fgSpec = getSpec(seriesIdOf(line.finishedGood, PANEL_SHOP_SITE));
    if (fgSpec) fgHistory.set(line.finishedGood, rawHistoryFor(fgSpec));
  }
  return AXIS.history.map((_, t) => {
    const needed = lines.reduce(
      (total, line) =>
        total + line.qtyPer * (fgHistory.get(line.finishedGood)?.[t] ?? 0),
      0,
    );
    return Math.max(0, Math.round(needed * (1 + 0.03 * gaussian(rand))));
  });
}
